// ═══════════════════════════════════════════════════════════════════════════════
// SUPERPOUVOIR NOTES VOCALES V2 - Deux modes intelligents
// Mode Réunion : Enregistrer une réunion/RDV → Compte-rendu structuré
// Mode Commande : Dictée intelligente → Détection auto (tâches, notes, idées, RDV...)
// ═══════════════════════════════════════════════════════════════════════════════

import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Mic, Square, Play, Pause, Trash2, Send, CheckCircle2,
  ListTodo, FileText, Mail, Loader2, AlertCircle, Clock,
  User, Briefcase, Sparkles, Volume2, Users, MessageSquare,
  Lightbulb, Calendar, Receipt, X, ChevronRight, Check,
  Video, Phone, Building
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// ═══════════════════════════════════════════════════════════════════════════════
// COMPOSANT PRINCIPAL
// ═══════════════════════════════════════════════════════════════════════════════
export default function VoiceNotesSuperpouvoir({ userId, onClose, onResult }) {
  // Mode sélectionné
  const [mode, setMode] = useState(null); // 'reunion' | 'commande'
  
  // État d'enregistrement
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  
  // État de traitement
  const [isProcessing, setIsProcessing] = useState(false);
  const [result, setResult] = useState(null);
  
  // Contexte pour mode Réunion
  const [reunionType, setReunionType] = useState('reunion'); // reunion, rdv, appel
  const [projectName, setProjectName] = useState('');
  
  // Participants avec autocomplétion
  const [participantInput, setParticipantInput] = useState('');
  const [selectedParticipants, setSelectedParticipants] = useState([]);
  const [participantSuggestions, setParticipantSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const participantInputRef = useRef(null);
  
  // Refs
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  
  // Timer pour l'enregistrement
  useEffect(() => {
    if (isRecording && !isPaused) {
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
    } else {
      clearInterval(timerRef.current);
    }
    return () => clearInterval(timerRef.current);
  }, [isRecording, isPaused]);
  
  // Recherche de participants avec debounce
  useEffect(() => {
    const searchParticipants = async () => {
      if (participantInput.length < 2) {
        setParticipantSuggestions([]);
        setShowSuggestions(false);
        return;
      }
      
      setIsSearching(true);
      try {
        const token = localStorage.getItem('ecosystem_token') || localStorage.getItem('client_token');
        if (!token) {
          setParticipantSuggestions([]);
          return;
        }
        
        const response = await fetch(`${API_URL}/api/batiment/participants/search?q=${encodeURIComponent(participantInput)}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          // Filter out already selected participants
          const filtered = (data.results || []).filter(
            p => !selectedParticipants.some(sp => sp.id === p.id)
          );
          setParticipantSuggestions(filtered);
          setShowSuggestions(filtered.length > 0);
        }
      } catch (err) {
        console.error('Erreur recherche participants:', err);
      } finally {
        setIsSearching(false);
      }
    };
    
    const timeoutId = setTimeout(searchParticipants, 300);
    return () => clearTimeout(timeoutId);
  }, [participantInput, selectedParticipants]);
  
  // Ajouter un participant sélectionné
  const addParticipant = (participant) => {
    setSelectedParticipants(prev => [...prev, participant]);
    setParticipantInput('');
    setShowSuggestions(false);
    participantInputRef.current?.focus();
  };
  
  // Supprimer un participant
  const removeParticipant = (participantId) => {
    setSelectedParticipants(prev => prev.filter(p => p.id !== participantId));
  };
  
  // Vérifier si l'utilisateur est connecté
  const isUserLoggedIn = () => {
    return !!(localStorage.getItem('ecosystem_token') || localStorage.getItem('client_token'));
  };
  
  // Formater le temps
  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // Démarrer l'enregistrement
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      mediaRecorderRef.current = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        setAudioBlob(blob);
        setAudioUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorderRef.current.start(1000);
      setIsRecording(true);
      setRecordingTime(0);
      setResult(null);
      
    } catch (error) {
      console.error('Erreur micro:', error);
      toast.error("Impossible d'accéder au microphone");
    }
  };
  
  // Arrêter l'enregistrement
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      setIsPaused(false);
    }
  };
  
  // Pause/Resume
  const togglePause = () => {
    if (mediaRecorderRef.current) {
      if (isPaused) {
        mediaRecorderRef.current.resume();
      } else {
        mediaRecorderRef.current.pause();
      }
      setIsPaused(!isPaused);
    }
  };
  
  // Supprimer l'enregistrement
  const deleteRecording = () => {
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    setResult(null);
  };
  
  // Retour à la sélection de mode
  const backToModeSelection = () => {
    setMode(null);
    deleteRecording();
  };
  
  // Traiter l'enregistrement
  const processRecording = async () => {
    if (!audioBlob) return;
    
    setIsProcessing(true);
    
    try {
      const reader = new FileReader();
      reader.readAsDataURL(audioBlob);
      
      reader.onloadend = async () => {
        const base64Audio = reader.result.split(',')[1];
        
        const endpoint = mode === 'reunion' 
          ? '/api/voice-notes/process-reunion'
          : '/api/voice-notes/process-commande';
        
        const payload = {
          audio_base64: base64Audio,
          audio_mime_type: 'audio/webm',
          duration_seconds: recordingTime,
          user_id: userId,
          // Contexte spécifique au mode
          ...(mode === 'reunion' && {
            reunion_type: reunionType,
            project_name: projectName || null,
            participants: selectedParticipants.length > 0 
              ? selectedParticipants.map(p => ({ 
                  id: p.id, 
                  name: p.name, 
                  type: p.type 
                }))
              : null
          })
        };
        
        const response = await fetch(`${API_URL}${endpoint}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        
        if (!response.ok) {
          const error = await response.json();
          throw new Error(error.detail || 'Erreur de traitement');
        }
        
        const data = await response.json();
        setResult(data);
        setIsProcessing(false);
        
        // Notification de succès
        const itemsCount = (data.tasks?.length || 0) + (data.notes?.length || 0) + 
                          (data.ideas?.length || 0) + (data.events?.length || 0);
        
        toast.success('Traitement terminé !', {
          description: mode === 'reunion' 
            ? 'Compte-rendu généré avec succès'
            : `${itemsCount} éléments détectés et classés`
        });
        
        // Callback
        if (onResult) onResult(data);
      };
      
    } catch (error) {
      console.error('Erreur traitement:', error);
      toast.error('Erreur de traitement', { description: error.message });
      setIsProcessing(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // ÉCRAN DE SÉLECTION DU MODE
  // ═══════════════════════════════════════════════════════════════════════════
  const renderModeSelection = () => (
    <div className="p-6 space-y-6">
      <div className="text-center mb-2">
        <p className="text-gray-600 text-sm">Choisis ton mode d'enregistrement</p>
      </div>
      
      <div className="grid grid-cols-2 gap-4">
        {/* Mode Réunion */}
        <button
          onClick={() => setMode('reunion')}
          className="group p-5 bg-gradient-to-br from-[#1E3A5F]/5 to-[#1E3A5F]/10 hover:from-[#1E3A5F] hover:to-[#2d5a8a] border-2 border-[#1E3A5F]/20 hover:border-[#1E3A5F] rounded-2xl transition-all duration-300 text-left"
          data-testid="mode-reunion-btn"
        >
          <div className="w-12 h-12 bg-[#1E3A5F]/10 group-hover:bg-white/20 rounded-xl flex items-center justify-center mb-4 transition-colors">
            <Users className="w-6 h-6 text-[#1E3A5F] group-hover:text-white transition-colors" />
          </div>
          <h3 className="font-bold text-[#1E3A5F] group-hover:text-white text-lg mb-1 transition-colors">
            Réunion / RDV
          </h3>
          <p className="text-sm text-gray-500 group-hover:text-white/80 transition-colors">
            Enregistre et obtiens un compte-rendu structuré
          </p>
        </button>
        
        {/* Mode Commande */}
        <button
          onClick={() => setMode('commande')}
          className="group p-5 bg-gradient-to-br from-[#F97316]/5 to-[#F97316]/10 hover:from-[#F97316] hover:to-[#EA580C] border-2 border-[#F97316]/20 hover:border-[#F97316] rounded-2xl transition-all duration-300 text-left"
          data-testid="mode-commande-btn"
        >
          <div className="w-12 h-12 bg-[#F97316]/10 group-hover:bg-white/20 rounded-xl flex items-center justify-center mb-4 transition-colors">
            <MessageSquare className="w-6 h-6 text-[#F97316] group-hover:text-white transition-colors" />
          </div>
          <h3 className="font-bold text-[#F97316] group-hover:text-white text-lg mb-1 transition-colors">
            Dictée intelligente
          </h3>
          <p className="text-sm text-gray-500 group-hover:text-white/80 transition-colors">
            Dicte et l'IA classe automatiquement
          </p>
        </button>
      </div>
      
      {/* Ce que l'IA détecte */}
      <div className="bg-gray-50 rounded-xl p-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">
          L'IA détecte et classe automatiquement
        </p>
        <div className="grid grid-cols-3 gap-2">
          {[
            { icon: ListTodo, label: 'Tâches', color: 'text-blue-600 bg-blue-50' },
            { icon: FileText, label: 'Notes', color: 'text-green-600 bg-green-50' },
            { icon: Lightbulb, label: 'Idées', color: 'text-yellow-600 bg-yellow-50' },
            { icon: Calendar, label: 'RDV', color: 'text-purple-600 bg-purple-50' },
            { icon: Mail, label: 'Emails', color: 'text-red-600 bg-red-50' },
            { icon: Receipt, label: 'Devis', color: 'text-orange-600 bg-orange-50' },
          ].map((item, idx) => (
            <div key={idx} className={`flex items-center gap-2 px-3 py-2 rounded-lg ${item.color}`}>
              <item.icon className="w-4 h-4" />
              <span className="text-xs font-medium">{item.label}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // ÉCRAN MODE RÉUNION
  // ═══════════════════════════════════════════════════════════════════════════
  const renderReunionMode = () => (
    <div className="p-6 space-y-5">
      {/* Retour */}
      <button
        onClick={backToModeSelection}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#1E3A5F] transition-colors"
      >
        <ChevronRight className="w-4 h-4 rotate-180" />
        Changer de mode
      </button>
      
      {!audioBlob && !isRecording && (
        <>
          {/* Type de réunion */}
          <div>
            <label className="text-sm font-medium text-gray-700 mb-2 block">Type d'enregistrement</label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: 'reunion', icon: Users, label: 'Réunion' },
                { id: 'rdv', icon: Building, label: 'RDV Client' },
                { id: 'appel', icon: Phone, label: 'Appel' },
              ].map((type) => (
                <button
                  key={type.id}
                  onClick={() => setReunionType(type.id)}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                    reunionType === type.id
                      ? 'border-[#1E3A5F] bg-[#1E3A5F]/5'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <type.icon className={`w-5 h-5 ${reunionType === type.id ? 'text-[#1E3A5F]' : 'text-gray-400'}`} />
                  <span className={`text-xs font-medium ${reunionType === type.id ? 'text-[#1E3A5F]' : 'text-gray-600'}`}>
                    {type.label}
                  </span>
                </button>
              ))}
            </div>
          </div>
          
          {/* Infos contextuelles */}
          <div className="space-y-3">
            {/* Participant avec autocomplétion */}
            <div className="relative">
              <label className="text-sm text-gray-600 mb-1 block">
                Participant(s) <span className="text-gray-400 text-xs">(sépare par virgule)</span>
              </label>
              
              {/* Tags des participants sélectionnés */}
              {selectedParticipants.length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                  {selectedParticipants.map((p) => (
                    <span
                      key={p.id}
                      className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                        p.type === 'client' 
                          ? 'bg-green-100 text-green-700' 
                          : p.type === 'prospect'
                            ? 'bg-blue-100 text-blue-700'
                            : 'bg-gray-100 text-gray-700'
                      }`}
                    >
                      {p.name}
                      <button
                        onClick={() => removeParticipant(p.id)}
                        className="hover:text-red-500 transition-colors"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              
              <div className="relative">
                <input
                  ref={participantInputRef}
                  type="text"
                  value={participantInput}
                  onChange={(e) => setParticipantInput(e.target.value)}
                  onFocus={() => participantSuggestions.length > 0 && setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  placeholder={isUserLoggedIn() ? "Rechercher un contact..." : "Connecte-toi pour ajouter des participants"}
                  disabled={!isUserLoggedIn()}
                  className={`w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F] ${!isUserLoggedIn() ? 'bg-gray-50 cursor-not-allowed' : ''}`}
                />
                {isSearching && (
                  <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 animate-spin" />
                )}
              </div>
              
              {/* Message si pas connecté */}
              {!isUserLoggedIn() && (
                <div className="mt-2 p-3 bg-gradient-to-r from-[#1E3A5F]/5 to-[#F97316]/5 border border-[#1E3A5F]/10 rounded-lg">
                  <p className="text-xs text-[#1E3A5F] font-medium mb-1">
                    Crée ton compte gratuit pour sauvegarder tes contacts !
                  </p>
                  <p className="text-xs text-gray-500">
                    🎁 <span className="font-semibold text-[#F97316]">1 000 AbracaCoins offerts</span> pour tester toutes les fonctionnalités
                  </p>
                  <a 
                    href="/reseau/inscription" 
                    className="inline-block mt-2 px-3 py-1.5 bg-[#1E3A5F] text-white text-xs font-medium rounded-lg hover:bg-[#2d5a8a] transition-colors"
                  >
                    Créer mon compte gratuit
                  </a>
                </div>
              )}
              
              {/* Message si connecté mais aucun résultat */}
              {isUserLoggedIn() && participantInput.length >= 2 && !isSearching && participantSuggestions.length === 0 && (
                <div className="mt-2 p-2 bg-gray-50 border border-gray-200 rounded-lg">
                  <p className="text-xs text-gray-500">
                    Aucun contact trouvé. <a href="/cockpit-pro" className="text-[#1E3A5F] font-medium hover:underline">Ajoute des prospects/clients</a> dans ton Cockpit.
                  </p>
                </div>
              )}
              
              {/* Suggestions dropdown */}
              {showSuggestions && participantSuggestions.length > 0 && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                  {participantSuggestions.map((suggestion) => (
                    <button
                      key={suggestion.id}
                      onClick={() => addParticipant(suggestion)}
                      className="w-full px-3 py-2 text-left hover:bg-gray-50 flex items-center gap-2 border-b border-gray-100 last:border-0"
                    >
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        suggestion.type === 'client' 
                          ? 'bg-green-100 text-green-700' 
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {suggestion.type === 'client' ? 'Client' : 'Prospect'}
                      </span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{suggestion.name}</p>
                        {suggestion.company && (
                          <p className="text-xs text-gray-500 truncate">{suggestion.company}</p>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
            
            {/* Projet */}
            <div>
              <label className="text-sm text-gray-600 mb-1 block">Projet (optionnel)</label>
              <input
                type="text"
                value={projectName}
                onChange={(e) => setProjectName(e.target.value)}
                placeholder="Nom du projet"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-[#1E3A5F]/20 focus:border-[#1E3A5F]"
              />
            </div>
          </div>
        </>
      )}
      
      {/* Zone d'enregistrement */}
      <div className="text-center space-y-4">
        {/* Visualisation - CLIQUABLE */}
        <div 
          className="relative w-32 h-32 mx-auto cursor-pointer"
          onClick={() => {
            if (!isRecording && !audioBlob) {
              startRecording();
            } else if (isRecording) {
              stopRecording();
            }
          }}
          data-testid="mic-circle-reunion"
        >
          <motion.div
            className={`absolute inset-0 rounded-full ${
              isRecording 
                ? 'bg-red-500/20' 
                : audioBlob 
                  ? 'bg-green-500/20' 
                  : 'bg-[#1E3A5F]/10'
            }`}
            animate={isRecording && !isPaused ? {
              scale: [1, 1.3, 1],
              opacity: [0.5, 0.2, 0.5]
            } : {}}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
          <div className={`absolute inset-2 rounded-full flex items-center justify-center ${
            isRecording 
              ? 'bg-red-500' 
              : audioBlob 
                ? 'bg-green-500' 
                : 'bg-[#1E3A5F] hover:bg-[#2d5a8a]'
          } text-white shadow-lg transition-colors`}>
            {isRecording ? (
              <div className="text-center">
                <Mic className="w-6 h-6 mx-auto mb-1" />
                <span className="text-sm font-mono">{formatTime(recordingTime)}</span>
              </div>
            ) : audioBlob ? (
              <CheckCircle2 className="w-10 h-10" />
            ) : (
              <Mic className="w-10 h-10" />
            )}
          </div>
        </div>
        
        {/* Texte d'aide */}
        {!isRecording && !audioBlob && (
          <p className="text-sm text-gray-500">
            Clique sur le micro pour commencer
          </p>
        )}
        
        {/* Contrôles */}
        <div className="flex items-center justify-center gap-3">
          {isRecording && (
            <>
              <button
                onClick={togglePause}
                className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
              >
                {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
              </button>
              <button
                onClick={stopRecording}
                className="p-4 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
              >
                <Square className="w-5 h-5" />
              </button>
            </>
          )}
          
          {audioBlob && !isRecording && (
            <>
              <button
                onClick={deleteRecording}
                className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
              >
                <Trash2 className="w-5 h-5 text-gray-600" />
              </button>
              <audio src={audioUrl} controls className="h-10" />
            </>
          )}
        </div>
        
        {/* Bouton traitement */}
        {audioBlob && !isRecording && (
          <button
            onClick={processRecording}
            disabled={isProcessing}
            className="w-full py-4 bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a] text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Analyse en cours...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Générer le compte-rendu
              </>
            )}
          </button>
        )}
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // ÉCRAN MODE COMMANDE
  // ═══════════════════════════════════════════════════════════════════════════
  const renderCommandeMode = () => (
    <div className="p-6 space-y-5">
      {/* Retour */}
      <button
        onClick={backToModeSelection}
        className="flex items-center gap-1 text-sm text-gray-500 hover:text-[#1E3A5F] transition-colors"
      >
        <ChevronRight className="w-4 h-4 rotate-180" />
        Changer de mode
      </button>
      
      {/* Zone d'enregistrement - BOUTONS EN PREMIER */}
      <div className="text-center space-y-4">
        {/* Visualisation - CLIQUABLE */}
        <div 
          className="relative w-32 h-32 mx-auto cursor-pointer"
          onClick={() => {
            if (!isRecording && !audioBlob) {
              startRecording();
            } else if (isRecording) {
              stopRecording();
            }
          }}
          data-testid="mic-circle-commande"
        >
          <motion.div
            className={`absolute inset-0 rounded-full ${
              isRecording 
                ? 'bg-[#1E3A5F]/20' 
                : audioBlob 
                  ? 'bg-green-500/20' 
                  : 'bg-[#1E3A5F]/10'
            }`}
            animate={isRecording && !isPaused ? {
              scale: [1, 1.3, 1],
              opacity: [0.5, 0.2, 0.5]
            } : {}}
            transition={{ duration: 1.5, repeat: Infinity }}
          />
          <div className={`absolute inset-2 rounded-full flex items-center justify-center ${
            isRecording 
              ? 'bg-[#1E3A5F]' 
              : audioBlob 
                ? 'bg-green-500' 
                : 'bg-[#1E3A5F] hover:bg-[#2d5a8a]'
          } text-white shadow-lg transition-colors`}>
            {isRecording ? (
              <div className="text-center">
                <Mic className="w-6 h-6 mx-auto mb-1" />
                <span className="text-sm font-mono">{formatTime(recordingTime)}</span>
              </div>
            ) : audioBlob ? (
              <CheckCircle2 className="w-10 h-10" />
            ) : (
              <Mic className="w-10 h-10" />
            )}
          </div>
        </div>
        
        {/* Texte d'aide */}
        {!isRecording && !audioBlob && (
          <p className="text-sm text-gray-500">
            Clique sur le micro pour commencer
          </p>
        )}
        
        {/* Contrôles - BOUTONS BLEUS */}
        <div className="flex items-center justify-center gap-3">
          {isRecording && (
            <>
              <button
                onClick={togglePause}
                className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
              >
                {isPaused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
              </button>
              <button
                onClick={stopRecording}
                className="p-4 bg-[#1E3A5F] text-white rounded-full hover:bg-[#2d5a8a] transition-colors"
              >
                <Square className="w-5 h-5" />
              </button>
            </>
          )}
          
          {audioBlob && !isRecording && (
            <>
              <button
                onClick={deleteRecording}
                className="p-3 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors"
              >
                <Trash2 className="w-5 h-5 text-gray-600" />
              </button>
              <audio src={audioUrl} controls className="h-10" />
            </>
          )}
        </div>
        
        {/* Bouton traitement - BLEU */}
        {audioBlob && !isRecording && (
          <button
            onClick={processRecording}
            disabled={isProcessing}
            className="w-full py-4 bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a] text-white rounded-xl font-semibold hover:shadow-lg transition-all disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isProcessing ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Traitement en cours...
              </>
            ) : (
              <>
                <Sparkles className="w-5 h-5" />
                Analyser et classer
              </>
            )}
          </button>
        )}
      </div>
      
      {/* Instructions - EN DESSOUS des boutons */}
      {!isRecording && !audioBlob && (
        <div className="bg-[#F0F4F8] border border-[#1E3A5F]/10 rounded-xl p-4">
          <p className="text-sm text-[#1E3A5F] font-medium mb-2">💡 Exemples de ce que tu peux dire :</p>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• "Créer une tâche : rappeler Martin demain"</li>
            <li>• "Note : le client préfère le carrelage gris"</li>
            <li>• "Idée : proposer un service de maintenance"</li>
            <li>• "RDV vendredi 14h avec M. Dupont"</li>
            <li>• "Envoyer un mail au fournisseur pour le devis"</li>
          </ul>
        </div>
      )}
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // ÉCRAN RÉSULTATS
  // ═══════════════════════════════════════════════════════════════════════════
  const renderResults = () => (
    <div className="p-6 space-y-5 overflow-y-auto max-h-[60vh]">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-[#1E3A5F] text-lg">
          {mode === 'reunion' ? '📋 Compte-rendu' : '✨ Éléments détectés'}
        </h3>
        <button
          onClick={() => {
            setResult(null);
            deleteRecording();
          }}
          className="text-sm text-[#F97316] hover:underline"
        >
          Nouvel enregistrement
        </button>
      </div>
      
      {/* Transcription */}
      {result?.transcription && (
        <div className="bg-gray-50 rounded-xl p-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Transcription</p>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{result.transcription}</p>
        </div>
      )}
      
      {/* Résumé (mode réunion) */}
      {result?.summary && (
        <div className="bg-[#1E3A5F]/5 border border-[#1E3A5F]/20 rounded-xl p-4">
          <p className="text-xs font-semibold text-[#1E3A5F] uppercase tracking-wide mb-2">Résumé</p>
          <p className="text-sm text-gray-700 whitespace-pre-wrap">{result.summary}</p>
        </div>
      )}
      
      {/* Tâches */}
      {result?.tasks?.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-blue-600 flex items-center gap-2">
            <ListTodo className="w-4 h-4" />
            Tâches créées ({result.tasks.length})
          </p>
          <div className="space-y-2">
            {result.tasks.map((task, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                <Check className="w-4 h-4 text-blue-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-800">{task.title}</p>
                  {task.due_date && (
                    <p className="text-xs text-gray-500">Échéance : {task.due_date}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Notes */}
      {result?.notes?.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-green-600 flex items-center gap-2">
            <FileText className="w-4 h-4" />
            Notes enregistrées ({result.notes.length})
          </p>
          <div className="space-y-2">
            {result.notes.map((note, idx) => (
              <div key={idx} className="p-3 bg-green-50 rounded-lg">
                <p className="text-sm text-gray-800">{note.content}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Idées */}
      {result?.ideas?.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-yellow-600 flex items-center gap-2">
            <Lightbulb className="w-4 h-4" />
            Idées ajoutées à la Boîte à idées ({result.ideas.length})
          </p>
          <div className="space-y-2">
            {result.ideas.map((idea, idx) => (
              <div key={idx} className="p-3 bg-yellow-50 rounded-lg">
                <p className="text-sm text-gray-800">{idea.content}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Événements/RDV */}
      {result?.events?.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-purple-600 flex items-center gap-2">
            <Calendar className="w-4 h-4" />
            RDV ajoutés à l'agenda ({result.events.length})
          </p>
          <div className="space-y-2">
            {result.events.map((event, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                <Calendar className="w-4 h-4 text-purple-600 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-800">{event.title}</p>
                  {event.date && (
                    <p className="text-xs text-gray-500">{event.date}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Emails */}
      {result?.emails?.length > 0 && (
        <div className="space-y-2">
          <p className="text-sm font-semibold text-red-600 flex items-center gap-2">
            <Mail className="w-4 h-4" />
            Brouillons d'emails créés ({result.emails.length})
          </p>
          <div className="space-y-2">
            {result.emails.map((email, idx) => (
              <div key={idx} className="p-3 bg-red-50 rounded-lg">
                <p className="text-sm font-medium text-gray-800">À : {email.to}</p>
                <p className="text-sm text-gray-600">{email.subject}</p>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Actions finales */}
      <div className="flex gap-3 pt-4">
        <button
          onClick={onClose}
          className="flex-1 py-3 bg-[#1E3A5F] text-white rounded-xl font-medium hover:bg-[#2d5a8a] transition-colors"
        >
          Terminé
        </button>
      </div>
    </div>
  );

  // ═══════════════════════════════════════════════════════════════════════════
  // RENDER PRINCIPAL
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full overflow-hidden" data-testid="voice-notes-superpouvoir">
      {/* Header - Fond bleu marine avec texte BLANC */}
      <div className="bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a] p-5 text-white relative">
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-2 hover:bg-white/10 rounded-full transition-colors"
          data-testid="close-superpouvoir-btn"
        >
          <X className="w-5 h-5 text-white" />
        </button>
        
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur flex items-center justify-center">
            <Mic className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-white">Note vocale IA</h2>
            <p className="text-white/80 text-sm">
              {mode === 'reunion' ? 'Mode Réunion / RDV' : 
               mode === 'commande' ? 'Mode Dictée intelligente' : 
               'Enregistre, je m\'occupe du reste'}
            </p>
          </div>
        </div>
      </div>
      
      {/* Content */}
      {result ? (
        renderResults()
      ) : mode === null ? (
        renderModeSelection()
      ) : mode === 'reunion' ? (
        renderReunionMode()
      ) : (
        renderCommandeMode()
      )}
    </div>
  );
}
